/**
 * The registration flow is configured in Tarh Portal (Features → Registration flow) and resolved by the
 * backend per registration type and entity type. `flow_state` on the subscription status carries the
 * active steps in sequence plus `next_step`; this module maps that to routes so no step order is
 * hard-coded in the app.
 */

export const FLOW_STEP = {
  SUBSCRIPTION_PLANS: "subscription_plans",
  PAYMENT: "payment",
  PAYMENT_VERIFICATION: "payment_verification",
  VAT: "vat_information",
  FIELD_OF_WORK: "field_of_work",
  AUTHORIZATION_LETTER: "authorization_letter",
  AUTHORIZATION_LETTER_PENDING: "authorization_letter_pending",
};

export const FLOW_STEP_ROUTES = {
  [FLOW_STEP.SUBSCRIPTION_PLANS]: "/subscribe",
  [FLOW_STEP.PAYMENT]: "/subscribe",
  [FLOW_STEP.PAYMENT_VERIFICATION]: "/bank-transfer/pending",
  [FLOW_STEP.VAT]: "/vat",
  [FLOW_STEP.FIELD_OF_WORK]: "/field-of-work-classification?next=flow",
  [FLOW_STEP.AUTHORIZATION_LETTER]: "/declaration",
  [FLOW_STEP.AUTHORIZATION_LETTER_PENDING]: "/declaration/pending",
};

export function isFlowStepActive(flowState, code) {
  const steps = Array.isArray(flowState?.steps) ? flowState.steps : null;
  // Before the backend reports steps (older API), every step is considered active.
  if (!steps) return true;
  return steps.some((step) => step?.code === code);
}

export function isPaymentWaived(flowState) {
  return flowState?.payment_waived === true;
}

/**
 * Where an authenticated user has to go before they may use the platform, or null when nothing is pending.
 *
 * @param {object|null} flowState  `subscriptions.flowState`
 * @param {object} ctx
 * @param {boolean} ctx.subscribed             an active subscription exists
 * @param {boolean} ctx.isExpired              the latest subscription has expired (the dashboard offers renewal)
 * @param {string|null} ctx.bankTransferStepRedirect  pending/rejected bank transfer screen, if any
 */
export function flowRedirect(flowState, { subscribed, isExpired, bankTransferStepRedirect }) {
  const next = flowState?.next_step || null;

  if (next === FLOW_STEP.PAYMENT_VERIFICATION) {
    return bankTransferStepRedirect || FLOW_STEP_ROUTES[FLOW_STEP.PAYMENT_VERIFICATION];
  }
  if (bankTransferStepRedirect) {
    return bankTransferStepRedirect;
  }

  if (!subscribed) {
    // No active subscription: choose a plan, unless an expired one is being renewed from the dashboard.
    return isExpired ? null : FLOW_STEP_ROUTES[FLOW_STEP.SUBSCRIPTION_PLANS];
  }

  if (!next || next === FLOW_STEP.SUBSCRIPTION_PLANS || next === FLOW_STEP.PAYMENT) {
    return null;
  }

  return FLOW_STEP_ROUTES[next] || null;
}

/** True when `redirect` points at the screen currently shown. */
export function redirectMatchesPath(redirect, pathname) {
  if (!redirect) return false;
  return redirect.split("?")[0] === pathname;
}
